import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";

import Print, { type PrintHandle } from "@components/print/print";

import kutil, { type PageInfo } from "@modules/kutil";

import dayjs from "dayjs";

import "./print_ticket.scoped.scss";

const CHUNK_SIZE = 5;

// CLS(ctg) → 영문 1자리 코드
const CTG_CODE_MAP: Record<string, string> = {
  "EXPENDABLE": "C",
  "NSN REPAIRABLE": "B",
  "SN REPAIRABLE": "A",
  "ROTABLE": "A",
};

function toCtgCode(ctg?: string): string {
  if (!ctg) return "";
  const key = ctg.trim().toUpperCase();
  return CTG_CODE_MAP[key] ?? ctg;
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

interface TicketPrintState {
  printDate: string;
  infos: any[];        // 목록 페이지용 (비어있으면 ticket-list HTML 안 그림)
  pdfUrls: string[];
}

const defTicketPrintState: TicketPrintState = {
  printDate: "",
  infos: [],
  pdfUrls: []
};

export type TicketType = 1 | 2 | "M";
export interface PrintTicketParams {
  infos: any[];
  ticketType?: TicketType | string;
}

export interface PrintTicketHandle {
  print: (params: PrintTicketParams) => Promise<void>;
}

const PrintTicket = forwardRef<PrintTicketHandle>((_, ref) => {
  const printRef = useRef<PrintHandle>(null);
  const [state, setState] = useState<TicketPrintState>(defTicketPrintState);

  useImperativeHandle(ref, () => ({
    print: async ({ infos, ticketType = 1 }) => {
      if (!infos?.length) return;

      const type = String(ticketType);
      const isMulti = type === "M";
      const copies = isMulti ? 1 : Number(type) || 1;

      // 목록형은 2건 이상일 때만 REMARK 일치 확인
      if (isMulti && infos.length > 1) {
        const hasMismatch = infos.some(
          (info) => info.deliveryTo !== infos[0].deliveryTo || info.remark !== infos[0].remark
        );
        if (hasMismatch && (await kutil.confirm("REMARK 정보가 다릅니다. 계속 출력하시겠습니까?")) === false) {
          return;
        }
      }

      // 선택 건수 × 매수 (1장/2장 모두 동일 규칙, 2장은 같은 티켓 연속 2장)
      const pdfUrls = infos.flatMap((info) =>
        Array.from(
          { length: copies },
          () => "/api/inoutbound/inout_work.download?mode=downloadTicketFile&workReqErpNo=" + encodeURIComponent(info.workReqErpNo)
        )
      );

      flushSync(() => {
        setState({
          printDate: dayjs().format("YYYY-MMM-DD, HH:mm"),
          infos: isMulti ? infos : [],   // 목록형 페이지는 multi에서만 (1건이어도 생성)
          pdfUrls,
        });
      });

      await printRef.current?.print();
      setState(defTicketPrintState);
    },
  }));

  const ticketPages = state?.infos ? chunkArray(state.infos, CHUNK_SIZE) : [];

  function renderTicketItem(info: any, idx: number) {
    return (
      <div key={idx}>
        <div className="ticket-list-type1">
          <p>PN</p>
          <span>{info.pn}</span>
          <p>TR</p>
          <span>{info.trNo}</span>
          <span>{info.reqQty}</span>
        </div>
        <div className="ticket-list-type1">
          <p>DES</p>
          <span>{info.kwd}</span>
          <p>SN</p>
          <span>{info.sn}</span>
          <span>{info.uom}</span>
        </div>
        <div className="ticket-list-type2">
          <p>ID</p>
          <span>{info.trackingId}</span>
          <p>WO</p>
          <span>{info.wo}</span>
          <p>CLS</p>
          <span>{toCtgCode(info.ctg)}</span>
          <p>CON</p>
          <span>{info.conditionContent}</span>
        </div>
        <div className="ticket-list-type3">
          <p>TO</p>
          <span>{info.toOrg}, {info.toSubinv}, {info.workShop}</span>
          <p>DEP</p>
          <span>{info.dept}</span>
        </div>
      </div>
    );
  }

  return (
    <Print ref={printRef}>
      {state?.pdfUrls && state.pdfUrls.length > 0 && (
        // 110 * 175
        <Print.Pdf pdfUrls={state.pdfUrls} width={110} height={undefined} />
      )}

      {ticketPages.map((page, pageIdx) => (
        <div className="print-ticket-list" key={pageIdx}>
          <div>
            <div className="ticket-top-area">
              <p>ISSUE MANIFEST</p>
              <div>
                <p>PRINTED</p>
                <span>{ state?.printDate }</span>
              </div>
            </div>
            <div className="ticket-list">
              {page.map((info, idx) => renderTicketItem(info, idx))}
            </div>
            <div className="ticket-bottom-area">
              <div>
                <p>DELIVERTY TO</p>
                <span>{state?.infos?.[0]?.deliveryTo}</span>
                <p>REMARK</p>
                <span>{state?.infos?.[0]?.remark}</span>
              </div>
              <p>RECEIPIENT</p>
            </div>
          </div>
        </div>
      ))}
    </Print>
  );
});

export default React.memo(PrintTicket);
